import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

export interface Localita {
  id: string;
  nome: string;
}

export interface Ateco {
  _id: string;
  desc: string;
}

export interface Nace {
  _id: string;
  desc: string;
}

export interface Paese {
  countryName: string;
  countryCode: string;
  callingCode: string;
  flag: string;
}

@Injectable({
  providedIn: 'root',
})
export class JsonFileService {
  private readonly jsonRegioni = 'assets/json/regioni.json';
  private readonly jsonComuni = 'assets/json/comuni.json';
  private readonly jsonProvince = 'assets/json/province.json';
  private readonly jsonAteco = 'assets/json/ateco.json';
  private readonly jsonNace = 'assets/json/nace.json';
  private readonly jsonPaesi = 'assets/json/paesi.json';

  constructor(private readonly http: HttpClient) {}

  /**
   * Lettura del file json delle regioni italiane
   * @returns regioni
   */
  public getRegioni(): Observable<Localita[]> {
    return this.http.get<Localita[]>(this.jsonRegioni).pipe(
      map((regioni: any[]) => {
        return regioni.map((item) => ({
          id: item.id,
          nome: item.nome,
        }));
      }),
    );
  }

  public getRegioniByNome(regione: string): Observable<Localita[]> {
    return this.http.get<Localita[]>(this.jsonRegioni).pipe(
      map((regioni: any[]) => {
        return regioni
          .filter((item) => item.nome === regione)
          .map((filteredItem) => ({
            id: filteredItem.id,
            nome: filteredItem.nome,
          }));
      }),
    );
  }

  /**
   * Lettura del file json delle Province italiane
   * @returns province
   */
  public getProvince(id: string): Observable<any> {
    return this.http.get<Localita[]>(this.jsonProvince).pipe(
      map((province: any[]) => {
        return province
          .filter((item) => item.id_regione === id) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            id: filteredItem.id,
            nome: filteredItem.nome,
          }));
      }),
    );
  }

  public getProvinceByNome(provincia: string): Observable<any> {
    return this.http.get<Localita[]>(this.jsonProvince).pipe(
      map((province: any[]) => {
        return province
          .filter((item) => item.nome === provincia) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            id: filteredItem.id,
            nome: filteredItem.nome,
          }));
      }),
    );
  }
  /**
   * Lettura del file json dei Comuni italiani
   * @returns comuni
   */
  public getComuni(id: string): Observable<Localita[]> {
    return this.http.get<Localita[]>(this.jsonComuni).pipe(
      map((comuni: any[]) => {
        return comuni
          .filter((item) => item.id_provincia === id) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            id: filteredItem.id,
            nome: filteredItem.nome,
          }));
      }),
    );
  }

  public getComuniByNome(comune: string): Observable<Localita[]> {
    return this.http.get<Localita[]>(this.jsonComuni).pipe(
      map((comuni: any[]) => {
        return comuni
          .filter((item) => item.nome === comune) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            id: filteredItem.id,
            nome: filteredItem.nome,
          }));
      }),
    );
  }

  public getAteco(): Observable<Ateco[]> {
    return this.http.get<Ateco[]>(this.jsonAteco).pipe(
      map((ateco: any[]) => {
        return ateco.map((item) => ({
          _id: item._id,
          desc: item.desc,
        }));
      }),
    );
  }
  public getNace(): Observable<Nace[]> {
    return this.http.get<Nace[]>(this.jsonNace).pipe(
      map((nace: any[]) => {
        return nace.map((item) => ({
          _id: item._id || item.section,
          desc: item.desc,
        }));
      }),
    );
  }

  public getPaesi(): Observable<Paese[]> {
    return this.http.get<Paese[]>(this.jsonPaesi).pipe(
      map((paese: any[]) => {
        return paese.map((item) => ({
          countryCode: item.countryCode,
          countryName: item.countryName,
          callingCode: item.callingCode,
          flag: item.flag,
        }));
      }),
    );
  }

  public getAtecoFiltrato(ricerca: string | undefined, ids?: string[]): Observable<Ateco[]> {
    return this.http.get<Ateco[]>(this.jsonAteco).pipe(
      map((ateco: any[]) => {
        if (!ricerca) {
          let atecoFiltrati = ateco;
          if (ids) {
            atecoFiltrati = atecoFiltrati.filter((atecoF) => ids.includes(atecoF._id));
          }
          return atecoFiltrati
            .map((item) => ({
              _id: item._id,
              desc: item.desc,
            }))
            .slice(0, 190); // Limita a 90 record
        }
        return ateco
          .filter(
            (item) =>
              item._id.toLowerCase().includes(ricerca.toLowerCase()) ||
              item.desc.toLowerCase().includes(ricerca.toLowerCase()),
          )
          .map((item) => ({
            _id: item._id,
            desc: item.desc,
          }))
          .slice(0, 190); // Limita a 90 record
      }),
    );
  }

  public getNaceFiltrato(ricerca: string | undefined, ids?: string[]): Observable<Nace[]> {
    return this.http.get<Nace[]>(this.jsonNace).pipe(
      map((nace: any[]) => {
        if (!ricerca) {
          let naceFiltrati = nace;
          if (ids) {
            naceFiltrati = naceFiltrati.filter((naceF) => ids.includes(naceF._id));
          }
          return naceFiltrati
            .map((item) => ({
              _id: item._id,
              desc: item.desc,
            }))
            .slice(0, 190); // Limita a 90 record
        }
        return nace
          .filter(
            (item) =>
              item._id.toLowerCase().includes(ricerca.toLowerCase()) ||
              item.desc.toLowerCase().includes(ricerca.toLowerCase()),
          )
          .map((item) => ({
            _id: item._id,
            desc: item.desc,
          }))
          .slice(0, 190); // Limita a 90 record
      }),
    );
  }

  public getAtecoById(idAteco: string[]): Observable<Ateco[]> {
    return this.http.get<Ateco[]>(this.jsonAteco).pipe(
      map((ateco: any[]) => {
        return ateco
          .filter((item) => idAteco.indexOf(item._id) > -1) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            _id: filteredItem._id,
            desc: filteredItem.desc,
          }));
      }),
    );
  }
  public getNaceById(idNace: string[]): Observable<Nace[]> {
    return this.http.get<Nace[]>(this.jsonNace).pipe(
      map((nace: any[]) => {
        return nace
          .filter((item) => idNace.includes(item._id) || idNace.includes(item.section)) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            _id: filteredItem._id || filteredItem.section,
            desc: filteredItem.desc,
          }));
      }),
    );
  }

  public getPaeseById(idPaese: string[]): Observable<Paese[]> {
    return this.http.get<Paese[]>(this.jsonPaesi).pipe(
      map((nace: any[]) => {
        return nace
          .filter((item) => idPaese.indexOf(item.countryCode) > -1) // Filtra gli elementi in base all'id
          .map((filteredItem) => ({
            countryCode: filteredItem.countryCode,
            countryName: filteredItem.countryName,
            callingCode: filteredItem.callingCode,
            flag: filteredItem.flag,
          }));
      }),
    );
  }
}
